import { useEffect, useMemo, useState } from "react";
import type { Guest } from "@/types/Guest";
import { getGuestsByIds } from "@/lib/api/handlers/GuestHandling";
import {
  MAIN_GUEST_FETCH_FIELDS,
  withMainGuestVariables,
} from "@/lib/utils/main-guest-variables";

/**
 * Returns `guest` with the `mainGuest` substitution-context key bound to its
 * (sanitized) MAIN GUEST, so `{{mainGuest.<field>}}` tokens resolve in preview
 * exactly like a real send (`main-consumer` `enrichMainGuest`).
 *
 * When the recipient is a companion (`mainGuest` id present) the main guest is
 * fetched once by id through the normal guest API — which applies the viewer's
 * FIELD-SCOPE masking — so preview shows exactly the fields the user may see.
 * The pre-joined `mainGuestDTO` from the guest-list pool only carries the name
 * (the `$lookup` projects `firstName`/`lastName` only), so it can't be trusted
 * to resolve `{{mainGuest.email}}` / `{{mainGuest.additionalFields.X}}`; it's
 * used only as an instant placeholder until the fetch lands.
 *
 * EMPTY-SAFE: recipients with no main guest (the recipient IS a main guest)
 * keep `mainGuest` undefined, so every `{{mainGuest.*}}` resolves to "". No
 * fetch is attempted for them. Returns `null` for a null input guest.
 */
export function useMainGuestVariables(
  eventId: string | undefined,
  guest: Guest | null | undefined,
): Guest | null {
  // Keyed by the main-guest id so a stale fetch from a previously-previewed
  // recipient is never applied to the current one (the memo below ignores a
  // `fetched` whose id doesn't match `mainGuestId`, so switching recipients
  // shows placeholder-then-correct, never the prior recipient's main guest).
  const [fetched, setFetched] = useState<{ id: string; guest: Guest } | null>(
    null,
  );

  const mainGuestId = guest?.mainGuest;

  useEffect(() => {
    if (!eventId || !mainGuestId) return;
    let cancelled = false;
    getGuestsByIds(eventId, [mainGuestId], "", [], MAIN_GUEST_FETCH_FIELDS, 0, 1)
      .then((res) => {
        if (cancelled) return;
        if (Array.isArray(res) && res[0]) {
          setFetched({ id: mainGuestId, guest: res[0] });
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [eventId, mainGuestId]);

  return useMemo(() => {
    if (!guest) return null;
    // Prefer the full fetched record (all whitelisted fields); fall back to the
    // pool's name-only DTO as an instant placeholder until the fetch resolves.
    const usableFetch =
      fetched && fetched.id === guest.mainGuest ? fetched.guest : null;
    const dto = usableFetch ?? guest.mainGuestDTO;
    const withDto = dto ? { ...guest, mainGuestDTO: dto } : guest;
    return withMainGuestVariables(withDto);
  }, [guest, fetched]);
}
